import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';

import { useAppContext } from '../AppContext';
import ConnectionHubSkeleton from './ConnectionHubSkeleton';
import InteractiveConstellation from './InteractiveConstellation';
import ManageConnections from './ManageConnections';
import { CosmicAlignmentReportModal, StarlightSurgeModal, ChartCourseModal } from './ConnectionModals';
import LevelingGuideModal from './LevelingGuideModal';

function ConnectionHub() {
    const { t } = useTranslation();
    const { userConnections, userProfiles, currentUserProfile, handleUserSelect, appFunctions, showConfirmation, setMessage, userId, db, appId, collection, getDocs } = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [constellationData, setConstellationData] = useState([]);
    const [interConnections, setInterConnections] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showSurgeModal, setShowSurgeModal] = useState(false);
    const [showCourseModal, setShowCourseModal] = useState(false);
    const [showAlignmentModal, setShowAlignmentModal] = useState(false);
    const [alignmentReport, setAlignmentReport] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [showLevelingGuide, setShowLevelingGuide] = useState(false);
    const [isActionsPanelOpen, setIsActionsPanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('constellation'); // New state for tabs

    const getXpForLevel = (level) => {
        if (level <= 1) return 100;
        return Math.floor(100 * Math.pow(level, 1.8));
    };

    useEffect(() => {
        const processConstellationData = async () => {
            if (currentUserProfile && userProfiles.length > 0 && userConnections) {
                const finalConstellationData = [{ ...currentUserProfile, connection: null }];
                const addedIds = new Set([userId]);
                userConnections.forEach(conn => {
                    const profile = userProfiles.find(p => p.id === conn.followingId);
                    if (profile && !addedIds.has(profile.id)) {
                        finalConstellationData.push({ ...profile, connection: conn });
                        addedIds.add(profile.id);
                    }
                });
                setConstellationData(finalConstellationData);

                const directConnectionIds = finalConstellationData.slice(1).map(c => c.id);
                if (directConnectionIds.length > 1) {
                    const interConnectPromises = directConnectionIds.map(id => getDocs(collection(db, `artifacts/${appId}/users/${id}/connections`)));
                    const interConnectSnapshots = await Promise.all(interConnectPromises);
                    const newInterConnections = [];
                    interConnectSnapshots.forEach((snapshot, index) => {
                        const sourceId = directConnectionIds[index];
                        snapshot.forEach(doc => {
                            const targetId = doc.data().followingId;
                            if (directConnectionIds.includes(targetId) && !newInterConnections.some(l => (l.source === targetId && l.target === sourceId))) {
                                newInterConnections.push({ source: sourceId, target: targetId });
                            }
                        });
                    });
                    setInterConnections(newInterConnections);
                }
                setIsLoading(false);
            }
        };
        processConstellationData();
    }, [currentUserProfile, userProfiles, userConnections, userId, db, appId, collection, getDocs]);

    const handleSelectStar = (profile) => {
        setSelectedProfile(profile.id === userId ? null : profile);
    };

    const handleGetAlignmentReport = () => {
        showConfirmation({
            message: t('connectionHub.alignmentReportConfirmation'),
            onConfirm: async () => {
                setIsAnalyzing(true);
                try {
                    const getCosmicAlignmentReport = httpsCallable(appFunctions, 'getCosmicAlignmentReport');
                    const result = await getCosmicAlignmentReport();
                    setAlignmentReport(result.data);
                    setShowAlignmentModal(true);
                } catch (error) {
                    setMessage(t('connectionHub.analysisFailed', { error: error.message }));
                } finally {
                    setIsAnalyzing(false);
                }
            }
        });
    };

    if (isLoading || !currentUserProfile) {
        return <ConnectionHubSkeleton />;
    }

    const level = currentUserProfile.constellationLevel || 1;
    const currentXp = currentUserProfile.constellationXP || 0;
    const xpToNextLevel = getXpForLevel(level);
    const xpProgress = level >= 99 ? 100 : (currentXp / xpToNextLevel) * 100;

    const LevelBar = () => (
        <div className="bg-gray-800/70 backdrop-blur-md rounded-lg border border-gray-700/50 p-4">
            <div className="flex justify-between items-center text-sm font-bold mb-1">
                <span className="text-cyan-300">{t('connectionHub.constellationLevel', { level })}</span>
                <span className="text-gray-400">{level >= 99 ? t('connectionHub.maxLevel') : `${currentXp} / ${xpToNextLevel} XP`}</span>
            </div>
            <div className="w-full bg-black/50 rounded-full h-4 border-2 border-cyan-900/50 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${xpProgress}%` }}></div>
            </div>
        </div>
    );

    const ActionsPanelContent = () => (
        <>
            <h2 className="text-2xl font-bold text-blue-200 font-playfair mb-4 text-center">{t('connectionHub.cosmicActions')}</h2>
            {selectedProfile ? (
                <div className="text-center animate-fadeIn">
                    <img src={selectedProfile.photoURL || "https://placehold.co/100x100/AEC6CF/FFFFFF?text=U"} alt={selectedProfile.displayName} className="w-24 h-24 rounded-full mx-auto border-4 border-purple-400 cursor-pointer" onClick={() => handleUserSelect(selectedProfile.id)} />
                    <h3 className="text-xl font-bold mt-3">{selectedProfile.displayName}</h3>
                    <p className="text-xs text-cyan-300 mb-4">{t('connectionHub.constellationLvl', { level: selectedProfile.constellationLevel || 1 })}</p>
                    <div className="space-y-3">
                        <button onClick={() => setShowSurgeModal(true)} className="w-full small-action-button bg-yellow-500 hover:bg-yellow-400 text-black text-base py-3 justify-start px-4"><LucideIcons.Sun size={18} className="mr-3" /> {t('connectionHub.starlightSurge')}</button>
                        <button onClick={() => setShowCourseModal(true)} className="w-full small-action-button bg-indigo-500 hover:bg-indigo-600 text-base py-3 justify-start px-4"><LucideIcons.Map size={18} className="mr-3" /> {t('connectionHub.chartACourse')}</button>
                    </div>
                </div>
            ) : (
                <div className="text-center text-gray-400 py-8">
                    <LucideIcons.MousePointerClick size={48} className="mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-white">{t('connectionHub.selectAStar')}</h3>
                    <p className="mt-2">{t('connectionHub.selectAStarDescription')}</p>
                    <div className="flex flex-col gap-3 mt-6">
                        <button onClick={handleGetAlignmentReport} disabled={isAnalyzing} className="w-full small-action-button bg-cyan-600 hover:bg-cyan-700 text-base py-3 justify-center disabled:opacity-50">
                            {isAnalyzing ? <div className="action-spinner" /> : <><LucideIcons.BrainCircuit size={18} className="mr-3" /> {t('connectionHub.alignmentReport')}</>}
                        </button>
                        <button onClick={() => setShowLevelingGuide(true)} className="w-full small-action-button bg-gray-600 hover:bg-gray-700 text-base py-3 justify-center">
                            <LucideIcons.BookOpen size={18} className="mr-3" /> {t('connectionHub.levelingGuide')}
                        </button>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className="animate-fadeIn">
            <Helmet>
                <title>Connection Hub - Whispers of Harmony</title>
                <meta name="description" content="Manage your connections and form deep bonds in Whispers of Harmony." />
            </Helmet>
            {showSurgeModal && <StarlightSurgeModal targetProfile={selectedProfile} onClose={() => setShowSurgeModal(false)} />}
            {showCourseModal && <ChartCourseModal targetProfile={selectedProfile} onClose={() => setShowCourseModal(false)} />}
            {showAlignmentModal && <CosmicAlignmentReportModal report={alignmentReport} onClose={() => setShowAlignmentModal(false)} />}
            {showLevelingGuide && <LevelingGuideModal onClose={() => setShowLevelingGuide(false)} />}

            <div className="relative w-full h-[85vh] lg:grid lg:grid-cols-3 lg:gap-6">

                <div className="absolute inset-0 lg:relative lg:col-span-2 mt-24 lg:mt-0">
                    <div className="flex justify-center border-b border-gray-700 mb-4">
                        <button onClick={() => setActiveTab('constellation')} className={`profile-tab-button ${activeTab === 'constellation' ? 'active' : ''}`}>{t('connectionHub.tabs.constellation')}</button>
                        <button onClick={() => setActiveTab('manage')} className={`profile-tab-button ${activeTab === 'manage' ? 'active' : ''}`}>{t('connectionHub.tabs.manage')}</button>
                    </div>

                    {activeTab === 'constellation' ? (
                        constellationData.length > 1 ? (
                            <InteractiveConstellation connections={constellationData} interConnections={interConnections} onSelectStar={handleSelectStar} />
                        ) : (
                            <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg h-full flex flex-col justify-center">
                                <LucideIcons.Users size={48} className="mx-auto mb-4 text-purple-400" />
                                <h3 className="text-xl font-bold">{t('connectionHub.quietCosmosTitle')}</h3>
                                <p className="mt-2">{t('connectionHub.quietCosmosDescription')}</p>
                            </div>
                        )
                    ) : (
                        <ManageConnections />
                    )}
                </div>

                <div className="absolute top-4 left-4 right-4 z-10 lg:hidden">
                    <LevelBar />
                </div>

                <div className="hidden lg:flex lg:flex-col lg:col-span-1 lg:gap-6">
                    <LevelBar />
                    <div className="bg-gray-800/70 backdrop-blur-md rounded-lg border border-gray-700/50 p-4 flex-grow">
                        <ActionsPanelContent />
                    </div>
                </div>

                <button
                    onClick={() => setIsActionsPanelOpen(true)}
                    className={`fixed bottom-0 left-1/2 -translate-x-1/2 z-20 items-center gap-2 px-6 py-2 bg-gray-800/70 backdrop-blur-md border-t border-l border-r border-gray-600 rounded-t-xl text-white font-semibold transition-transform duration-300 hover:scale-105 lg:hidden ${isActionsPanelOpen ? 'hidden' : 'flex'}`}
                >
                    <LucideIcons.Sparkles size={20} />
                    {t('connectionHub.cosmicActions')}
                </button>

                <div
                    className={`fixed bottom-0 left-0 right-0 z-30 p-4 transition-transform duration-500 ease-in-out lg:hidden ${isActionsPanelOpen ? 'translate-y-0' : 'translate-y-full'}`}
                >
                    <div className="bg-gray-800/80 backdrop-blur-xl rounded-t-2xl border-t border-gray-600 p-4 relative">
                        <button onClick={() => setIsActionsPanelOpen(false)} className="absolute top-3 right-3 ai-icon-button text-gray-400 hover:bg-white/10">
                            <LucideIcons.X size={24} />
                        </button>
                        <ActionsPanelContent />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConnectionHub;